/**
 * Diagnostics for the TRX hosted-field components — the record of WHY a
 * PayPage form failed, kept apart from the sentence the operator reads.
 *
 * WHY THIS EXISTS
 * ---------------
 * On 2026-10-13 a Books in-person bank entry on development showed "Payment
 * form failed to load" while the server side was healthy: TRX minted the
 * session ("00 Initialized") and returned clientId, payPageId and sessionId.
 * The failure was in the browser, and nothing said what it was:
 *   - `PayPageJS.initialize(...).catch(() => …)` discarded the rejection;
 *   - init-time `instance.errors` were mapped straight to copy, and four
 *     different TRX error types (iframe / container / styles / wrong-config)
 *     collapse to the same sentence;
 *   - an exception thrown inside our own `.then` landed in that same `.catch`
 *     and was reported as a TRX load failure.
 * No console line, no Sentry event, no TRX text on screen.
 *
 * TRX's bundle resolves `initialize()` with configuration problems in
 * `instance.errors` ("'sessionData' must contain a defined 'payPageId'
 * property", "Container with 'x' id is not found on your page", "Error loading
 * iframe for 'routing' field. URL is unavailable") and rejects only when
 * something throws. Both carriers are recorded here.
 *
 * WHAT A DIAGNOSTIC MAY CONTAIN
 * -----------------------------
 * Only TRX's error taxonomy (`errorType`, `fieldType`, `errorText`) and a
 * thrown error's `name` / `message`. TRX's errors describe configuration and
 * field validity; they never carry the card or bank number, and the session
 * triple is never read into a diagnostic. `toDiagnosticErrors` copies the three
 * whitelisted keys and nothing else, so an unexpected key on a future bundle
 * cannot ride along.
 *
 * This package reads no env and owns no reporter: every diagnostic goes to
 * `console.error`, and a host app that has an error reporter (Sentry in
 * management) passes it in through `TrxFieldsDiagnosticsProvider` or a
 * component's `onDiagnostic` prop.
 */
import {
  trxErrorField,
  trxErrorMessage,
  trxErrorType,
  type TrxPayPageError,
} from "./trxPayPageTypes";

/** Which step of the hosted-field lifecycle failed. */
export type TrxFieldsPhase = "script" | "initialize" | "extract";

/**
 * - `trx-errors`     — TRX reported errors (`instance.errors` / `result.errors`).
 * - `init-rejected`  — `PayPageJS.initialize` rejected (something threw in TRX's code).
 * - `callback-threw` — OUR handler threw after a successful init. A bug of ours,
 *                      never a TRX load failure.
 * - `script-load-failed` — TRX's PayPage-Fields bundle never loaded (timeout,
 *                      network/blocker, or no `window.PayPageJS`); `cause` names which.
 */
export type TrxFieldsDiagnosticKind =
  | "trx-errors"
  | "init-rejected"
  | "callback-threw"
  | "script-load-failed";

/** One TRX error, reduced to the keys a diagnostic may carry. */
export interface TrxFieldsDiagnosticError {
  readonly errorType: string;
  readonly fieldType?: string;
  readonly errorText: string;
}

export interface TrxFieldsDiagnostic {
  /** The component that hit the failure, e.g. `TrxAchFields`. */
  readonly component: string;
  readonly phase: TrxFieldsPhase;
  readonly kind: TrxFieldsDiagnosticKind;
  readonly errors: readonly TrxFieldsDiagnosticError[];
  /** The thrown value, for `init-rejected` / `callback-threw`. */
  readonly cause?: { readonly name: string; readonly message: string };
}

export type TrxFieldsDiagnosticHandler = (diagnostic: TrxFieldsDiagnostic) => void;

/** Long enough for any TRX sentence; short enough that a runaway string cannot flood a log. */
const MAX_TEXT_LENGTH = 300;

function clip(text: string): string {
  return text.length > MAX_TEXT_LENGTH ? `${text.slice(0, MAX_TEXT_LENGTH)}…` : text;
}

/** TRX errors → the three whitelisted keys, whichever spelling the bundle used. */
export function toDiagnosticErrors(
  errors: readonly TrxPayPageError[] | undefined,
): TrxFieldsDiagnosticError[] {
  return (errors ?? []).map((err) => {
    const fieldType = trxErrorField(err);
    return {
      errorType: trxErrorType(err) || "unknown",
      ...(fieldType !== undefined ? { fieldType } : {}),
      errorText: clip(trxErrorMessage(err)),
    };
  });
}

/** A thrown value's name and message — nothing else is read from it. */
export function describeThrown(cause: unknown): { name: string; message: string } {
  if (cause instanceof Error) {
    return { name: cause.name || "Error", message: clip(cause.message) };
  }
  return { name: typeof cause, message: clip(String(cause)) };
}

export function buildTrxFieldsDiagnostic(input: {
  readonly component: string;
  readonly phase: TrxFieldsPhase;
  readonly kind: TrxFieldsDiagnosticKind;
  readonly errors?: readonly TrxPayPageError[];
  readonly cause?: unknown;
}): TrxFieldsDiagnostic {
  return {
    component: input.component,
    phase: input.phase,
    kind: input.kind,
    errors: toDiagnosticErrors(input.errors),
    ...("cause" in input ? { cause: describeThrown(input.cause) } : {}),
  };
}

/**
 * True when every error is the payer's own input (`wrong-field`). Those are
 * shown to the operator by name and are not a defect, so the extract path does
 * not report them.
 */
export function isPayerInputOnly(errors: readonly TrxPayPageError[] | undefined): boolean {
  return (
    errors !== undefined &&
    errors.length > 0 &&
    errors.every((err) => trxErrorType(err) === "wrong-field")
  );
}

/**
 * Log a diagnostic and hand it to the host's reporter. A reporter that throws
 * is contained: diagnosis must never take the payment form down with it.
 */
export function emitTrxFieldsDiagnostic(
  diagnostic: TrxFieldsDiagnostic,
  onDiagnostic?: TrxFieldsDiagnosticHandler,
): void {
  console.error(
    `[TRX hosted fields] ${diagnostic.component} ${diagnostic.phase} failed (${diagnostic.kind})`,
    diagnostic,
  );
  if (!onDiagnostic) return;
  try {
    onDiagnostic(diagnostic);
  } catch (reporterError) {
    console.error("[TRX hosted fields] diagnostic reporter threw", describeThrown(reporterError));
  }
}
